import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight, FiArrowUpRight, FiCheck, FiExternalLink, FiImage, FiLayers, FiShield, FiZap } from 'react-icons/fi';
import SEO from '../components/SEO';
import { publishedProducts } from '../data/productsData';

const isExternal = (product) => Boolean(product.landingPageUrl);
const productUrl = (product) => product.landingPageUrl || '/products/' + product.slug;

function ProductLink({ product, children, className }) {
  if (product.status === 'coming-soon') {
    return <span className={className + ' cursor-default opacity-60'} aria-label={product.name + ' coming soon'}>{children}</span>;
  }
  if (isExternal(product)) {
    return <a className={className} href={productUrl(product)} target="_blank" rel="noopener noreferrer">{children}</a>;
  }
  return <Link className={className} to={productUrl(product)}>{children}</Link>;
}

function ProductGallery({ product, small = false }) {
  const [selected, setSelected] = useState(0);
  const [failedImage, setFailedImage] = useState(-1);
  const images = product.images || [];
  const active = images[selected];

  useEffect(() => {
    setSelected(0);
    setFailedImage(-1);
  }, [product.slug]);

  return (
    <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-lg shadow-slate-900/5">
      <div className="flex items-center justify-between px-1 pb-3 border-b border-slate-100 gap-2">
        <div className="flex items-center gap-1.5" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-rose-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
        </div>
        <span className="truncate text-[11px] font-semibold text-slate-500">{product.name} preview</span>
      </div>
      <div className={'relative mt-3 flex items-center justify-center overflow-hidden rounded-xl bg-slate-50 ' + (small ? 'h-44 sm:h-52' : 'h-56 sm:h-72 lg:h-80')}>
        {active && failedImage !== selected ? (
          <img
            src={active.url}
            alt={active.alt}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-contain"
            onError={() => setFailedImage(selected)}
          />
        ) : (
          <div className="flex flex-col items-center gap-3 p-6 text-center text-slate-500">
            <FiImage className="h-8 w-8 text-blue-500" aria-hidden="true" />
            <span className="text-sm font-semibold">{product.name}</span>
            <span className="text-xs">Explore the application to see it in action.</span>
          </div>
        )}
      </div>
      {images.length > 1 && (
        <div className="mt-3 grid grid-cols-5 gap-2" aria-label={product.name + ' screenshot gallery'}>
          {images.map((image, index) => (
            <button
              key={image.url}
              type="button"
              aria-label={'View ' + product.name + ' preview ' + (index + 1)}
              aria-pressed={selected === index}
              onClick={() => { setSelected(index); setFailedImage(-1); }}
              className={'h-12 sm:h-14 overflow-hidden rounded-lg border-2 bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ' + (selected === index ? 'border-blue-600' : 'border-transparent hover:border-slate-300')}
            >
              <img src={image.url} alt="" loading="lazy" decoding="async" className="h-full w-full object-contain" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FeaturedProduct({ product }) {
  return (
    <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5">
      <div className="grid grid-cols-1 items-center gap-8 p-5 sm:p-8 lg:grid-cols-2 lg:gap-12 lg:p-12">
        <div className="min-w-0 lg:order-2">
          <ProductGallery product={product} />
        </div>
        <div className="lg:order-1">
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">{product.category}</span>
            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">{product.status === 'live' ? 'Available now' : 'Coming soon'}</span>
          </div>
          <h3 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{product.name}</h3>
          <p className="mt-4 text-xl font-semibold leading-snug text-slate-800">{product.tagline}</p>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600">{product.description}</p>
          <ul className="mt-7 space-y-3">
            {(product.benefits || []).slice(0, 4).map(benefit => (
              <li key={benefit} className="flex items-start gap-3 text-sm text-slate-700 sm:text-base">
                <FiCheck className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ProductLink
              product={product}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-blue-600 px-7 py-3 text-sm font-bold text-white shadow-lg shadow-blue-600/15 transition-colors hover:bg-blue-700"
            >
              {product.ctaLabel || 'Explore product'}
              {isExternal(product) ? <FiArrowUpRight aria-hidden="true" /> : <FiArrowRight aria-hidden="true" />}
            </ProductLink>
            {isExternal(product) && product.status === 'live' && (
              <span className="inline-flex items-center justify-center gap-2 text-xs text-slate-500 sm:justify-start">
                <FiExternalLink aria-hidden="true" /> Opens the official product website
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function ProductCard({ product }) {
  const image = product.images?.[0];
  return (
    <article className="group flex min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl">
      <div className="flex h-52 items-center justify-center border-b border-slate-100 bg-slate-50 p-5">
        {image ? <img className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.02]" src={image.url} alt={image.alt} loading="lazy" decoding="async" /> : <FiLayers className="h-10 w-10 text-blue-500" aria-hidden="true" />}
      </div>
      <div className="flex flex-1 flex-col p-6">
        <div className="mb-3 flex items-center justify-between gap-2">
          <span className="text-xs font-bold uppercase tracking-wide text-blue-600">{product.category}</span>
          <span className="text-xs font-semibold text-slate-500">{product.status === 'live' ? 'Available' : 'Coming soon'}</span>
        </div>
        <h3 className="text-xl font-bold text-slate-900">{product.name}</h3>
        <p className="mt-2 text-sm font-medium text-slate-700">{product.tagline}</p>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{product.description}</p>
        <ProductLink product={product} className="mt-6 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-5 py-3 text-sm font-bold text-blue-700 hover:bg-blue-100">
          {product.ctaLabel || 'Explore product'} {isExternal(product) ? <FiArrowUpRight aria-hidden="true" /> : <FiArrowRight aria-hidden="true" />}
        </ProductLink>
      </div>
    </article>
  );
}

export default function Products() {
  const featured = publishedProducts.find(product => product.featured) || publishedProducts[0];
  const others = publishedProducts.filter(product => product.slug !== featured?.slug);
  const listSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Business Software Applications | BPA Solutions',
    description: 'Discover purpose-built BPA Solutions business applications.',
    url: 'https://www.bpasolutions.au/products'
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-white text-left">
      <SEO title="Business Software & Productivity Applications | BPA Solutions" description="Explore BPA Solutions business software, including StatementSync for converting PDF bank statements into Excel or CSV. Discover purpose-built applications." schema={listSchema} canonicalPath="/products" />
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50 py-16 sm:py-24">
        <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-28 h-80 w-80 rounded-full bg-blue-100/60 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 text-xs font-bold uppercase tracking-wider text-blue-700 shadow-sm"><FiZap aria-hidden="true" /> BPA Solutions Software</span>
            <h1 className="mt-6 text-4xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">Practical software for <span className="text-blue-600">smarter business.</span></h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-xl">Discover purpose-built applications that simplify everyday work, reduce manual tasks and help you get more from your business data.</p>
            <a href="#products-list" className="mt-8 inline-flex min-h-12 items-center gap-2 rounded-xl bg-slate-900 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-slate-800">Explore applications <FiArrowRight aria-hidden="true" /></a>
          </div>
        </div>
      </section>
      <section id="products-list" className="scroll-mt-24 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {featured ? (
            <>
              <div className="mb-8">
                <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-blue-600">Featured application</span>
                <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">Built for the way you work</h2>
              </div>
              <FeaturedProduct product={featured} />
              {others.length > 0 && (
                <div className="mt-16">
                  <h2 className="mb-8 text-2xl font-bold text-slate-900 sm:text-3xl">More applications</h2>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {others.map(product => <ProductCard key={product.slug} product={product} />)}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-10 text-center text-slate-600">New applications will be announced here.</div>
          )}
        </div>
      </section>
      <section className="border-t border-slate-200 bg-slate-50 py-14 sm:py-16">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-7 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-8">
          <div className="max-w-2xl">
            <div className="mb-2 flex items-center gap-2 text-blue-600"><FiShield aria-hidden="true" /><span className="text-xs font-bold uppercase tracking-widest">Looking for something else?</span></div>
            <h2 className="text-2xl font-bold text-slate-900">Explore our free business tools</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">Our free resources include calculators and practical tools to help you make more informed business decisions.</p>
          </div>
          <Link to="/resources" className="inline-flex min-h-12 shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-bold text-slate-800 transition-colors hover:border-blue-500 hover:text-blue-600">Browse free resources <FiArrowRight aria-hidden="true" /></Link>
        </div>
      </section>
    </main>
  );
}

// Reusable on-site sales page for future products that do not have their own site.
// Products with an existing landingPageUrl use that site instead of duplicating it here.
export function ProductDetail() {
  const { slug } = useParams();
  const product = publishedProducts.find(item => item.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!product || product.landingPageUrl || product.status === 'coming-soon') {
    return <Navigate to="/products" replace />;
  }

  return (
    <main className="min-h-screen bg-white text-left">
      <SEO title={product.name + ' | Business Software | BPA Solutions'} description={product.description} canonicalPath={'/products/' + product.slug} />
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-12 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Link to="/products" className="mb-8 inline-flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700"><FiArrowLeft aria-hidden="true" /> All products</Link>
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="text-xs font-bold uppercase tracking-wide text-blue-600">{product.category}</span>
              <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">{product.name}</h1>
              <p className="mt-5 text-xl font-semibold text-slate-800">{product.tagline}</p>
              <p className="mt-5 text-base leading-relaxed text-slate-600">{product.description}</p>
              {product.demoUrl && <a href={product.demoUrl} target="_blank" rel="noopener noreferrer" className="mt-7 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white hover:bg-blue-700">View demo <FiArrowUpRight aria-hidden="true" /></a>}
            </div>
            <ProductGallery product={product} />
          </div>
        </div>
      </section>
      {Boolean(product.benefits?.length) && (
        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8 text-2xl font-bold text-slate-900 sm:text-3xl">Why choose {product.name}?</h2>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {product.benefits.map(benefit => <div key={benefit} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-6 text-slate-700"><FiCheck className="mt-1 shrink-0 text-blue-600" aria-hidden="true" /><span>{benefit}</span></div>)}
            </div>
          </div>
        </section>
      )}
      {Boolean(product.pricing?.length) && (
        <section className="border-t border-slate-200 bg-slate-50 py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8 text-2xl font-bold text-slate-900">Choose your plan</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {product.pricing.map(plan => <div key={plan.name} className="rounded-2xl border border-slate-200 bg-white p-6"><h3 className="text-xl font-bold text-slate-900">{plan.name}</h3><p className="mt-3 text-2xl font-bold text-blue-600">{plan.priceLabel}</p>{plan.description && <p className="mt-3 text-sm text-slate-600">{plan.description}</p>}{plan.checkoutUrl && <a href={plan.checkoutUrl} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700">Choose plan <FiArrowUpRight aria-hidden="true" /></a>}</div>)}
            </div>
          </div>
        </section>
      )}
      {Boolean(product.faqs?.length) && (
        <section className="py-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8 text-2xl font-bold text-slate-900">Frequently asked questions</h2>
            <div className="space-y-3">{product.faqs.map(faq => <details key={faq.question} className="rounded-xl border border-slate-200 p-5"><summary className="cursor-pointer font-semibold text-slate-800">{faq.question}</summary><p className="mt-3 text-sm leading-relaxed text-slate-600">{faq.answer}</p></details>)}</div>
          </div>
        </section>
      )}
    </main>
  );
}